"use client"

import { useEffect } from "react"
import { usePathname, useSearchParams } from "next/navigation"

// Restores scroll position saved by language switcher
export default function ScrollRestorer() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    try {
      const pathWithoutLocale = pathname?.replace(/^\/[a-z]{2}/, '') || '/'
      const localeMatch = pathname?.match(/^\/([a-z]{2})\//)
      const locale = localeMatch ? localeMatch[1] : null
      if (!locale) return

      const key = `scroll:${pathWithoutLocale}:${locale}`
      const val = sessionStorage.getItem(key)
      if (val) {
        // we stored a JSON payload with { anchorId?, anchorOffset?, y }
        let payload: { anchorId?: string | null; anchorOffset?: number; y?: number } | null = null
        try {
          payload = JSON.parse(val)
        } catch (e) {
          payload = null
        }

        const n = payload && typeof payload.y === 'number' ? payload.y : Number(val)
        const anchorId = payload && payload.anchorId ? payload.anchorId : null
        const anchorOffset = payload && typeof payload.anchorOffset === 'number' ? payload.anchorOffset : 0
        const useAnchor = !!anchorId

        if (!Number.isNaN(n) || useAnchor) {
          // Attempt to restore after navigation. Reduced attempts to avoid scroll fighting.
          let attempts = 0
          const maxAttempts = 20 // ~1 second at 50ms per attempt
          const attemptDelay = 50 // ms

          const tryRestore = () => {
            attempts += 1
            try {
              if (useAnchor) {
                const el = document.getElementById(anchorId as string)
                if (el) {
                  try {
                    // scroll element into view first
                    el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' })
                    // then scroll back down by the saved offset to position the element
                    // at the same viewport offset it had before
                    if (anchorOffset !== 0) {
                      window.scrollBy({ top: anchorOffset, left: 0, behavior: 'auto' })
                    }
                    // Clear immediately after anchor restore to prevent retries
                    try { sessionStorage.removeItem(key) } catch (e) {}
                    return
                  } catch (e) {
                    window.scrollTo({ top: n || 0, left: 0, behavior: 'auto' })
                  }
                } else if (attempts >= 5) {
                  // Give up on anchor after 5 attempts, fallback to numeric
                  window.scrollTo({ top: n || 0, left: 0, behavior: 'auto' })
                  try { sessionStorage.removeItem(key) } catch (e) {}
                  return
                }
              } else {
                window.scrollTo({ top: n, left: 0, behavior: 'auto' })
                try { sessionStorage.removeItem(key) } catch (e) {}
                return
              }
            } catch (e) {}

            // if we've reached the desired position (within tolerance), clear the saved key
            try {
              const current = Math.round(window.scrollY || 0)
              if (typeof n === 'number' && !Number.isNaN(n) && Math.abs(current - Math.round(n)) <= 20) {
                try { sessionStorage.removeItem(key) } catch (e) {}
                return
              }
            } catch (e) {}

            if (attempts < maxAttempts) {
              requestAnimationFrame(() => setTimeout(tryRestore, attemptDelay))
            } else {
              try { sessionStorage.removeItem(key) } catch (e) {}
            }
          }

          // start the retry loop after a brief delay to let page settle
          requestAnimationFrame(() => setTimeout(tryRestore, 100))
        }
      }
    } catch (e) {
      // no-op
    }
  }, [pathname, searchParams])

  return null
}
